/**
 * Generation API types for the book generation pipeline.
 * Used by both client (polling) and server (status tracking).
 */
import { z } from 'zod';
import { PAGE_BORDER_STYLES } from './borderDecorations.js';
import { lastRegenFailureSchema } from './regeneration.js';
import type { FailureDetails } from './orderProjection.js';

/**
 * Steps in the generation pipeline, mapped from LangGraph node names.
 */
export const GENERATION_STEPS = [
  'starting',
  'generating_plan',
  'plan_review',
  'human_plan_review',
  'generating_character_sheet',
  'human_character_sheet_review',
  'generating_pages',
  'generating_images',
  'generating_cover',
  'generating_pdf',
  'completed',
  'failed',
] as const;

export type GenerationStep = (typeof GENERATION_STEPS)[number];

/**
 * Coarse-grained pipeline phase tag attached to LangGraph metadata for
 * observability (LangSmith filtering, Sentry span attributes, structured logs).
 *
 * Phases are coarser than `GenerationStep` — they describe which segment of the
 * pipeline the run is entering, not the precise step. Used by Phase 0a
 * observability fixes (see `docs/architecture/phase-0-decisions.md`).
 */
export const GENERATION_PHASES = [
  'plan',
  'character_sheet',
  'pages',
  'cover',
  'pdf',
  'recovery',
] as const;

export type GenerationPhase = (typeof GENERATION_PHASES)[number];

/** All possible failure reason values (single source of truth) */
export const FAILURE_REASONS = [
  'CONTENT_BLOCKED',
  'PLAN_GENERATION_FAILED',
  'CHARACTER_SHEET_FAILED',
  'PAGE_GENERATION_FAILED',
  'COVER_GENERATION_FAILED',
  'PDF_GENERATION_FAILED',
  // Issue #633 Task 8 — emitted by the supernode's `loopDetectionHook`
  // when the agent repeats the same tool call ≥5 times in a sliding
  // window. The hook throws a `PIPELINE_FAILED:LOOP_DETECTED:transient`
  // envelope so the route layer's `parsePipelineFailureError` writes
  // the correct slug instead of falling back to
  // `PAGE_GENERATION_FAILED:transient`.
  'LOOP_DETECTED',
  // Issue #677 — fine-grained reasons emitted by the page-gen / cover
  // subagents via the new `AgentError` taxonomy
  // (`apps/server/src/agents/authorAgent/errors/AgentError.ts`). These
  // exist so the route layer's `parsePipelineFailureError` can write a
  // specific slug into `orders.failure_reason` instead of the catch-all
  // `PAGE_GENERATION_FAILED`. They map onto the same five existing
  // `FAILURE_CONTEXTS` (no new contexts) via `CATEGORY_TO_CONTEXT`.
  'PIXEL_LIMIT_EXCEEDED',
  'PAGE_COUNT_MISMATCH',
  'PAGE_IMAGE_EMPTY',
  'GEMINI_SAFETY_BLOCK',
  'RECURSION_LIMIT_EXCEEDED',
  'GLOBAL_REGEN_BUDGET_EXHAUSTED',
  'PLAN_INVALID_PAGE_COUNT',
  // Issue #713 Task 2 — emitted by `generateStoryPlan`'s post-invoke
  // validator when Gemini returns a `pagePlan` entry with an empty
  // `setting` or `illustration` (Zod allows empty strings on
  // `z.string()`). Mirrors `PLAN_INVALID_PAGE_COUNT` as a COUNT_MISMATCH
  // category — retryable so the LangGraph retry policy re-runs the node.
  'PLAN_INVALID_FIELDS',
  'COMPOSITOR_FAILED',
  'GEMINI_QUOTA_EXCEEDED',
  'GEMINI_TRANSIENT',
  // Issue #720 — emitted by `generatePagesNode` when the handoff
  // `planJson` arrives as malformed JSON (e.g. Gemini appended
  // explanation prose after the closing `}`). The page-gen node first
  // attempts a repair pass (slice to the last top-level `}`); only if
  // that also fails do we throw with this reason. Classified as
  // TRANSIENT so the LangGraph node `retryPolicy` re-runs the node
  // instead of giving up at `globalRegenCount=0`.
  'PLAN_JSON_PARSE_FAILED',
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

/** Context describing the cause of a pipeline failure (single source of truth) */
export const FAILURE_CONTEXTS = [
  'content_blocked',
  'feedback_caused',
  'survey_caused',
  'transient',
  // Issue #589 — emitted when the failure is provably non-recoverable by
  // a retry (currently: Vertex AI 404 "Publisher Model … was not found"
  // for a model name that doesn't exist at the configured endpoint). The
  // universal Try Again retry path treats `permanent` as a signal that
  // surfacing "Edit My Book" is the only sensible recovery — retrying the
  // same call would deterministically reproduce the same 404.
  'permanent',
  // Issue #537 — `user_abandoned` was deleted alongside the structural-repair
  // HITL "Start over" action. No production path emits it now; the
  // recovery-planner / failure-gate / client error-block code paths that
  // branched on it have all been removed.
] as const;

export type FailureContext = (typeof FAILURE_CONTEXTS)[number];

/**
 * Failure reason slugs written by infrastructure paths that have no LangGraph
 * state to project from (startup recovery, recovery planner, SIGTERM drain).
 * Kept separate from `FAILURE_REASONS` because those values flow through the
 * agent's typed `RepairEnvelope` schemas and must stay agent-scoped. Both
 * enums are persisted into the same `orders.failure_reason` column.
 */
export const INFRA_FAILURE_REASONS = [
  'STARTUP_NO_CHECKPOINT',
  'STARTUP_MOCK_MODE_RESUME',
  'STARTUP_RECOVERY_ERROR',
  'RECOVERY_PLANNER_UNRECOVERABLE',
  'SHUTDOWN_DRAIN_TIMEOUT',
] as const;

export type InfraFailureReason = (typeof INFRA_FAILURE_REASONS)[number];

/**
 * Story plan data shown to the user for review.
 */
export const planReviewDataSchema = z.object({
  title: z.string(),
  plotSummary: z.string(),
  pageCount: z.number(),
  bookLanguage: z.string(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire that
   * surfaced this review payload. Optional on the read side — the projection
   * helper can be called against arbitrary state where no plan-review
   * interrupt is active. On the SUBMIT endpoint (`POST /:id/review-plan`)
   * the field is REQUIRED; the client must echo it back from the most recent
   * polling response. The client's submit helper throws if absent rather
   * than producing a 400.
   */
  interruptId: z.string().uuid().optional(),
  /**
   * Movie-teaser-style 2–3 paragraph synopsis written by the author agent
   * (spec `docs/specs/ai-brain-target.md` §5 / §11). Additive — older clients
   * that ignore the field continue to work; clients that consume it render a
   * teaser block alongside the plan summary. Null on legacy graph orders.
   */
  storyTeaser: z.string().nullable().optional(),
});

export type PlanReviewData = z.infer<typeof planReviewDataSchema>;

/**
 * Response from POST /api/orders/:id/start-generation
 */
export const startGenerationResponseSchema = z.object({
  threadId: z.string(),
  status: z.enum(['GENERATING', 'GENERATING_PLAN']),
});

export type StartGenerationResponse = z.infer<
  typeof startGenerationResponseSchema
>;

/**
 * Character sheet data shown to the user for review.
 * storagePath is persisted; imageUrl is generated on-demand from storagePath.
 */
export const characterSheetReviewDataSchema = z.object({
  storagePath: z.string(),
  imageUrl: z.string().optional(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire that
   * surfaced this review payload. Optional on the read side — the projection
   * helper can be called against arbitrary state where no CS-review interrupt
   * is active. On the SUBMIT endpoint (`POST /:id/review-character-sheet`)
   * the field is REQUIRED; the client must echo it back from the most recent
   * polling response. The client's submit helper throws if absent rather
   * than producing a 400.
   */
  interruptId: z.string().uuid().optional(),
});

export type CharacterSheetReviewData = z.infer<
  typeof characterSheetReviewDataSchema
>;

/**
 * Loading-stage payload for the frontend-driven, time-based loading bar
 * (`docs/LOADING.md`). Surfaced additively on every polling response so the
 * client can compute the bar percent purely from elapsed time without an
 * extra round-trip.
 *
 * `stage` is intentionally `z.string()` rather than a closed enum — the
 * server may add new stages over time and clients must tolerate unknown
 * slugs (per the project's permissive wire-schema rule for fields the
 * server may extend). The server's narrow `LoadingStage` union lives in
 * `apps/server/src/services/loadingStages.ts`.
 *
 * `null` when the order is in a terminal state (`PREVIEW_READY`,
 * `GENERATION_FAILED`, `PRINT_JOB_SUBMITTED`, `SHIPPED`, etc.) or before
 * generation has started (`PAID`, `INIT`, etc.). The client treats
 * `loadingStage === null` as "no active wait — render the next screen".
 */
export const loadingStagePayloadSchema = z.object({
  stage: z.string(),
  averageLoadingSeconds: z.number(),
  startedAt: z.string().datetime({ offset: true }),
});

export type LoadingStagePayload = z.infer<typeof loadingStagePayloadSchema>;

/**
 * Wire schema for the structured failure-details payload (issue #677). Mirrors
 * `FailureDetails` from `./orderProjection.ts` but lives here so the polling
 * response can validate it through Zod. Every field is optional so legacy
 * failure paths (infra failures, pre-AgentError envelopes) round-trip cleanly.
 *
 * `pageFailureDetailSchema.lastReason` is intentionally open (`z.string()`) —
 * the per-page recovery path may emit free-form hints that are NOT in
 * `FAILURE_REASONS`; clients display this verbatim and never branch on it.
 */
export const pageFailureDetailSchema = z.object({
  pageId: z.string(),
  retries: z.number(),
  lastReason: z.string(),
  forceAccepted: z.boolean(),
});

export const failureDetailsSchema = z.object({
  pages: z.array(pageFailureDetailSchema).optional(),
  globalRegenCount: z.number().optional(),
  lastSkillFailed: z.string().optional(),
}) satisfies z.ZodType<FailureDetails>;

/**
 * Response from GET /api/orders/:id/generation-status
 * Zod enums derived from FAILURE_REASONS/FAILURE_CONTEXTS arrays (single source of truth).
 */
export const generationStatusResponseSchema = z.object({
  step: z.enum(GENERATION_STEPS),
  /**
   * Failure slug. Accepts both agent failure reasons (`FAILURE_REASONS`) and
   * infra failure reasons (`INFRA_FAILURE_REASONS`). Wire format intentionally
   * loose — `z.string()` — so the server can introduce new slugs without
   * breaking older clients (clients display `failureMessage` for human copy).
   */
  failureReason: z.string().nullable().optional(),
  /** Context describing the cause of the failure (feedback, survey, transient) */
  failureContext: z.enum(FAILURE_CONTEXTS).nullable().optional(),
  /** AI-generated user-facing message explaining what triggered a content block */
  failureMessage: z.string().nullable().optional(),
  /**
   * Issue #677 — recovery class (e.g. `PERMANENT`, `TRANSIENT`, `SAFETY`,
   * `COUNT_MISMATCH`, `CAPACITY_EXCEEDED`) from the `AgentError` taxonomy.
   * Null for legacy failures that predate the taxonomy. Wire format open
   * (`z.string()`) per line 80 convention — the server can introduce new
   * categories without breaking older clients.
   */
  failureCategory: z.string().nullable().optional(),
  /**
   * Issue #677 — structured failure diagnostics (per-page retry audit,
   * global regen counter, last skill name). Null for legacy failures and
   * for infra failures that don't carry agent-side structure.
   */
  failureDetails: failureDetailsSchema.nullable().optional(),
  planReview: planReviewDataSchema.nullable(),
  characterSheetReview: characterSheetReviewDataSchema.nullable(),
  currentPage: z.number().nullable(),
  totalPages: z.number().nullable(),
  /** True when book artifacts are uploaded and the preview page is available */
  previewAvailable: z.boolean().optional(),
  /**
   * LangGraph thread id for this generation run. Surfaced to the client so
   * the failure UI can display it as part of the technical-details payload
   * (issue #493 Task 16, B8). Null when no thread has been allocated yet
   * (pre-generation states).
   */
  threadId: z.string().nullable().optional(),
  /**
   * Failure metadata from a regeneration attempt that failed and was
   * restored from backup (issue #500). Surfaces the dismissible banner on
   * `/book/preview/<id>` so the user knows the previous run failed without
   * destroying their book. Null/undefined when there is no pending regen
   * failure to surface.
   */
  lastRegenFailure: lastRegenFailureSchema.nullable().optional(),
  /**
   * Movie-teaser-style 2–3 paragraph synopsis written by the author agent
   * (spec `docs/specs/ai-brain-target.md` §5). Populated after plan
   * approval, mirrored durably onto `orders.story_teaser`. Surfaced
   * additively on every polling response so the plan-review screen and
   * downstream pages can display it without a wire-schema bump. Null on
   * legacy graph orders and pre-approval reads.
   */
  storyTeaser: z.string().nullable().optional(),
  /**
   * Final book title written by the author-agent (spec §17 / `docs/LOADING.md`).
   * Mirrored to `orders.book_title` on plan approval by `applyReviewSubmission`
   * (AI-brain rewrite finalization Task 7) so the loading UI can render
   * "Generating <title>..." once known. Surfaced additively on every polling
   * response — older clients ignore it; newer clients display the title
   * during the post-approval generation phases. Null on legacy graph orders
   * and pre-approval reads.
   */
  bookTitle: z.string().nullable().optional(),
  /**
   * Frontend-driven loading-bar input (`docs/LOADING.md`). The server
   * tells the client which stage the order is in, the typical wait time
   * for that stage, and when the wait started. The client animates the
   * bar 0 → 86 → 99 against `averageLoadingSeconds` and jumps to 100 on
   * a stage transition.
   *
   * Null when the order is in a terminal state or before generation has
   * started.
   */
  loadingStage: loadingStagePayloadSchema.nullable(),
});

export type GenerationStatusResponse = z.infer<
  typeof generationStatusResponseSchema
>;

/**
 * Request body for POST /api/orders/:id/review-plan
 */
export const reviewPlanRequestSchema = z.object({
  approved: z.boolean(),
  feedback: z.string().optional(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire the
   * client is responding to. The client echoes back the value it received in
   * `PlanReviewData.interruptId`.
   */
  interruptId: z.string().uuid(),
  /**
   * Client-generated UUID, stable across the lifetime of a single review form
   * (Phase 4, round-5 M3 + round-6 R6B-H1). Echoed back on every retry of the
   * same form so accidental double-POST collapses to a single durable row.
   * Optional on the wire while the client rolls out — when missing, the
   * server fills in a fresh `randomUUID()` so each submit still gets a unique
   * dedup key against the per-(thread, kind, interrupt_id) index.
   */
  reviewSubmissionId: z.string().uuid().optional(),
});

export type ReviewPlanRequest = z.infer<typeof reviewPlanRequestSchema>;

/**
 * Response from POST /api/orders/:id/review-plan
 */
export const reviewPlanResponseSchema = z.object({
  success: z.boolean(),
});

export type ReviewPlanResponse = z.infer<typeof reviewPlanResponseSchema>;

/**
 * Request body for POST /api/orders/:id/review-character-sheet
 *
 * Phase 1 split (`pageBorderStyleOverride` → typed enum + freeform feedback):
 *   - `pageBorderStyleId: PageBorderStyle | null` — typed enum override that
 *     mutates `surveyData.preferences.pageBorderStyle`.
 *   - `humanCharacterSheetFeedback: string | null` — freeform regen feedback
 *     consumed by the prompt builder. NOT concatenated into the typed enum.
 *
 * Backward compatibility: `borderStyle` (single string field) is still
 * accepted from older clients. The route handler dispatches it: when its
 * value matches a known `PAGE_BORDER_STYLES` entry, it is treated as
 * `pageBorderStyleId`; otherwise it is treated as
 * `humanCharacterSheetFeedback`. New explicit fields override `borderStyle`
 * when both are present.
 */
export const reviewCharacterSheetRequestSchema = z
  .object({
    approved: z.boolean(),
    feedback: z.string().optional(),
    /** Typed enum override for the survey's `pageBorderStyle`. */
    pageBorderStyleId: z.enum(PAGE_BORDER_STYLES).nullable().optional(),
    /**
     * Freeform regen feedback. Flows into the regen prompt builder as a
     * separate slot — never concatenated with the typed `pageBorderStyleId`.
     */
    humanCharacterSheetFeedback: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .nullable()
      .optional(),
    /**
     * Legacy field retained for backward compatibility with older clients.
     * Server dispatches: enum match → pageBorderStyleId; non-match →
     * humanCharacterSheetFeedback.
     */
    borderStyle: z.string().trim().min(1).max(500).optional(),
    /**
     * Server-generated UUID identifying the specific HITL interrupt fire the
     * client is responding to. The client echoes back the value it received
     * in `CharacterSheetReviewData.interruptId`.
     */
    interruptId: z.string().uuid(),
    /**
     * Client-generated UUID, stable across the lifetime of a single review
     * form (Phase 4, round-5 M3 + round-6 R6B-H1). Echoed back on every
     * retry of the same form so accidental double-POST collapses to a single
     * durable row. Optional on the wire while the client rolls out — when
     * missing, the server fills in a fresh `randomUUID()` so each submit
     * still gets a unique dedup key against the per-(thread, kind,
     * interrupt_id) index.
     */
    reviewSubmissionId: z.string().uuid().optional(),
  })
  .refine(
    (data) => !((data.borderStyle || data.pageBorderStyleId) && data.approved),
    {
      message:
        'border-style override can only be provided when approved is false',
      path: ['pageBorderStyleId'],
    }
  );

export type ReviewCharacterSheetRequest = z.infer<
  typeof reviewCharacterSheetRequestSchema
>;

/**
 * Response from POST /api/orders/:id/review-character-sheet
 */
export const reviewCharacterSheetResponseSchema = z.object({
  success: z.boolean(),
});

export type ReviewCharacterSheetResponse = z.infer<
  typeof reviewCharacterSheetResponseSchema
>;
